<template>
	<v-card :key="product.product.id" class="mb-1" elevation="1">
		<v-list-item three-line>
			<v-list-item-icon class="mr-3">
				<v-icon v-if="type == 'formula'" color="primary">$vuetify.icons.tcmIcon</v-icon>
				<v-icon v-if="type == 'supplement'" color="primary">mdi-pill</v-icon>
			</v-list-item-icon>
			<v-list-item-content>
				<v-list-item-title>
					<v-row no-gutters align="center">
						<v-col cols="12" md="5">
							<strong>{{product.product.name}}</strong>
							<template v-if="!product.product.complex_product && product.product.unit">
								<strong> ({{product.product.units_box}} {{product.product.unit.code}})</strong>
							</template>
							<v-tooltip top>
								<template v-slot:activator="{ on }">
									<v-btn icon @click="showProductInfo = true" v-on="on">
										<v-icon color="primary">info_outline</v-icon>
									</v-btn>
								</template>
								<span v-translate>Product Info</span>
							</v-tooltip>
							<template v-if="context == 'appointment'">
								<v-tooltip top v-if="product.product.discontinued">
									<template v-slot:activator="{ on }">
										<v-btn icon v-on="on">
											<v-icon color="red">mdi-flask-remove-outline</v-icon>
										</v-btn>
									</template>
									<span v-translate>Discontinued</span>
								</v-tooltip>
								<v-tooltip top v-if="Object.keys(product.interaction_drugs).length > 0">
									<template v-slot:activator="{ on }">
										<v-btn icon @click="showInteractionDrugs = true" v-on="on">
											<v-icon color="red">error</v-icon>
										</v-btn>
									</template>
									<span v-translate>Interaction Drugs</span>
								</v-tooltip>
								<v-tooltip top v-if="Object.keys(product.contraindications).length > 0">
									<template v-slot:activator="{ on }">
										<v-btn icon @click="showContraindications = true" v-on="on">
											<v-icon color="red">error_outline</v-icon>
										</v-btn>
									</template>
									<span v-translate>Contra-Indications</span>
								</v-tooltip>
							</template>
						</v-col>
					</v-row>
				</v-list-item-title>
				<v-list-item-subtitle>
					<v-row no-gutters align="center">
						<template v-if="product.product.external_entity">
							<v-icon class="mr-1" small color="primary">mdi-store</v-icon>
							<span class="mr-2">{{product.product.external_entity.name}}</span>
						</template>
						<template v-else-if="type == 'formula'">
							<v-icon class="mr-1" small color="primary">mdi-spa</v-icon>
							<span class="mr-2" v-translate>Classic</span>
						</template>
						<template v-if="product.product.classic || product.product.customizable || product.product.classic_custom || product.product.lab_custom">
							<v-icon class="mr-1" small color="primary">mdi-file-document-edit-outline</v-icon>
							<span v-if="product.product.classic_custom || product.product.lab_custom" v-translate>Customized</span>
							<span v-else v-translate>Customizable</span>
						</template>
						<v-spacer></v-spacer>
						<template v-if="product.product.storage">
							<v-icon class="mr-1" small color="primary">mdi-bottle-tonic-plus</v-icon>
							<span class="mr-2">{{product.product.storage.name}}</span>
						</template>
						<template v-if="product.product.presentation">
							<v-icon class="mr-1" small color="primary">mdi-water</v-icon>
							<span>{{product.product.presentation.name}}</span>
						</template>
					</v-row>
				</v-list-item-subtitle>
				<v-row no-gutters>
					<v-col cols="12" md="12">
						<v-form ref="posologyForm" v-model="posologyFormValid">
							<v-row no-gutters align="center" class="mt-2">
								<template v-if="context == 'appointment'">
									<v-col cols="12" v-for="(frequency, i) in product.frequency" :key="i">
										<v-row no-gutters class="mb-2">
											<v-col v-if="frequency.complex_product_unlisted_id" cols="12" md="12" class="pb-2">
												<span class="text-subtitle-2">{{frequency.complex_product_unlisted_presentation.name}}</span>
											</v-col>
											<v-col cols="12" md="1" style="min-width: 46px">
												<v-text-field
													dense
													@blur="editPosology(product, frequency)"
													:label="$gettext('PA')"
													filled
													hide-details
													v-model="frequency.morning"
													:disabled="cantEdit"
												></v-text-field>
											</v-col>
											<v-col cols="12" md="1" style="min-width: 46px">
												<v-text-field
													dense
													@blur="editPosology(product, frequency)"
													:label="$gettext('MM')"
													filled
													hide-details
													v-model="frequency.middle_morning"
													:disabled="cantEdit"
												></v-text-field>
											</v-col>
											<v-col cols="12" md="1" style="min-width: 46px">
												<v-text-field
													dense
													@blur="editPosology(product, frequency)"
													:label="$gettext('A')"
													filled
													hide-details
													v-model="frequency.half_day"
													:disabled="cantEdit"
												></v-text-field>
											</v-col>
											<v-col cols="12" md="1" style="min-width: 46px">
												<v-text-field
													dense
													@blur="editPosology(product, frequency)"
													:label="$gettext('MT')"
													filled
													hide-details
													v-model="frequency.middle_afternoon"
													:disabled="cantEdit"
												></v-text-field>
											</v-col>
											<v-col cols="12" md="1" style="min-width: 46px">
												<v-text-field
													dense
													@blur="editPosology(product, frequency)"
													:label="$gettext('J')"
													filled
													hide-details
													v-model="frequency.night"
													:disabled="cantEdit"
												></v-text-field>
											</v-col>
											<v-col cols="12" md="7">
												<v-text-field
													dense
													@blur="editPosology(product, frequency)"
													:label="$gettext('Aditional Info')"
													filled
													hide-details
													class="ml-3"
													@click="editAditionalInfo = true"
													v-model="frequency.aditional_info"
													:disabled="cantEdit"
												></v-text-field>
											</v-col>
										</v-row>
									</v-col>
										<v-dialog
											v-if="Object.keys(product.interaction_drugs).length > 0"
											v-model="showInteractionDrugs"
											max-width="1000"
										>
											<v-card>
												<v-card-title
													class="primary--text mb-2 secondary"
													v-translate
												>Interaction Drugs</v-card-title>
												<v-card-text>
													<v-simple-table>
														<template v-slot:default>
															<thead>
																<tr>
																	<th class="text-left" v-translate>Substance</th>
																	<th class="text-left" v-translate>Drug</th>
																	<th class="text-left" v-translate>Interaction</th>
																	<th class="text-left" v-translate>Bibliography</th>
																</tr>
															</thead>
															<tbody>
																<tr v-for="(interaction, i) in product.interaction_drugs" :key="i">
																	<td>{{ interaction.substance }}</td>
																	<td>{{ interaction.drug }}</td>
																	<th>
																		<span v-if="interaction.interaction">
																			<span v-if="interaction.interaction.category">{{ interaction.interaction.category.name }} - </span>
																			{{ interaction.interaction.name }}
																			<span v-if="interaction.interaction_info">({{ interaction.interaction_info }})</span>
																		</span>
																		<span v-else v-translate>Not defined</span>
																	</th>
																	<td>
																		<v-btn v-if="interaction.interaction" right icon small @click="getInteractionDrugBibliographicReferences(interaction.id)" :loading="loadingDrugBibliographicReferences">
																			<v-icon small>mdi-book-open-variant</v-icon>
																		</v-btn>
																		<span v-else v-translate>Not defined</span>
																	</td>
																</tr>
															</tbody>
														</template>
													</v-simple-table>
												</v-card-text>
												<v-divider></v-divider>
												<v-card-actions>
													<v-spacer></v-spacer>
													<v-btn color="primary darken-1" text @click="showInteractionDrugs = false">
														<translate>Close</translate>
													</v-btn>
												</v-card-actions>
											</v-card>
											<v-dialog
												v-model="showInteractionDrugsBibliographicReferences"
												max-width="800"
												scrollable
											>
												<v-card>
													<v-card-title
														class="primary--text mb-2 secondary"
														v-translate
													>Bibliographic References</v-card-title>
													<v-card-text>
														<v-card class="mb-1" v-for="bibliographicReference in interactionDrugBibliographicReferences" :key="bibliographicReference.id">
															<v-card-text>
																<span v-if="bibliographicReference.authors" class="mr-1">{{bibliographicReference.authors}}</span>
																<span v-if="bibliographicReference.year" class="mr-1">({{bibliographicReference.year}})</span>
																<span v-if="bibliographicReference.title" class="mr-1">{{bibliographicReference.title}}</span>
																<span v-if="bibliographicReference.published_in" class="mr-1">{{bibliographicReference.published_in}}</span>
																<span v-if="bibliographicReference.volume" class="mr-1">{{bibliographicReference.volume}}</span>
																<span v-if="bibliographicReference.pages" class="mr-1">{{bibliographicReference.pages}}</span>
																<span v-if="bibliographicReference.web_url" class="mr-1 blue--text" style="cursor: pointer" @click="openLink(bibliographicReference.web_url)">{{bibliographicReference.web_url}}</span>
															</v-card-text>
														</v-card>
													</v-card-text>
													<v-divider></v-divider>
													<v-card-actions>
														<v-spacer></v-spacer>
														<v-btn color="primary darken-1" text @click="showInteractionDrugsBibliographicReferences = false">
															<translate>Close</translate>
														</v-btn>
													</v-card-actions>
												</v-card>
											</v-dialog>
										</v-dialog>
										<v-dialog
											v-if="Object.keys(product.contraindications).length > 0"
											v-model="showContraindications"
											max-width="1000"
										>
											<v-card>
												<v-card-title
													class="primary--text mb-2 secondary"
													v-translate
												>Contraindications</v-card-title>
												<v-card-text>
													<v-simple-table>
														<template v-slot:default>
															<thead>
																<tr>
																	<th class="text-left" v-translate>Substance</th>
																	<th class="text-left" v-translate>Symptom/Disease</th>
																</tr>
															</thead>
															<tbody>
																<tr v-for="(icd, substance) in product.contraindications" :key="substance">
																	<td>{{ substance }}</td>
																	<td>{{icd.join(', ')}}</td>
																</tr>
															</tbody>
														</template>
													</v-simple-table>
												</v-card-text>
												<v-divider></v-divider>
												<v-card-actions>
													<v-spacer></v-spacer>
													<v-btn color="primary darken-1" text @click="showContraindications = false">
														<translate>Close</translate>
													</v-btn>
												</v-card-actions>
											</v-card>
										</v-dialog>
										<!-- <v-dialog v-model="editAditionalInfo" max-width="40%" scrollable>
											<v-card>
												<v-card-title class="title primary--text mb-2 secondary" v-translate>Aditional Info</v-card-title>
												<v-card-text>
													<v-row no-gutters class="mt-3">
														<v-col cols="12" sm="12">
															<v-textarea
																@blur="editPosology(product, frequency)"
																:label="$gettext('Aditional Info')"
																outlined
																hide-details
																class="ml-3"
																v-model="product.frequency.aditional_info"
																autofocus
															></v-textarea>
														</v-col>
													</v-row>
												</v-card-text>
												<v-divider></v-divider>
												<v-card-actions>
													<v-spacer></v-spacer>
													<v-btn color="primary darken-1" text @click="editAditionalInfo = false">
														<translate>Close</translate>
													</v-btn>
												</v-card-actions>
											</v-card>
										</v-dialog> -->
								</template>
							</v-row>
						</v-form>
					</v-col>
				</v-row>
			</v-list-item-content>
			<v-list-item-action>
				<template v-if="context == 'settings' && ($store.getters.spaceId == product.product.space_id || admin || (externalEntity && product.product.external_entity && externalEntity.id == product.product.external_entity.id))">
					<v-row no-gutters>
					<v-tooltip top>
						<template v-slot:activator="{ on }">
							<v-btn key="custom-edit" v-if="product.product.customizable" @click="showEditSubstances" icon v-on="on">
								<v-icon color="primary">edit</v-icon>
								<OpenFormulaSubstances v-model="showOpenFormulaSubstances" :product="product" @save="saveOpenFormula" :loading="loadingOpenFormulaSubstances" :saving="loadingSaveOpenFormula" />
							</v-btn>
							<v-btn key="edit" v-else icon :to="editProduct" v-on="on">
								<v-icon color="primary">edit</v-icon>
							</v-btn>
						</template>
						<span v-translate>Edit</span>
					</v-tooltip>
					<v-tooltip top>
						<template v-slot:activator="{ on }">
							<v-btn icon @click="showDeleteProduct = true" v-on="on">
								<v-icon color="primary">delete</v-icon>
								<DeleteConfirmationDialog v-model="showDeleteProduct" item="product" @delete="removeProduct" />
							</v-btn>
						</template>
						<span v-translate>Remove</span>
					</v-tooltip>
					<v-btn icon v-if="product.product.num_ingredients == 0">
						<v-icon color="red">mdi-checkbox-blank-circle</v-icon>
					</v-btn>
					<v-chip small :color="product.product.last_edit_user.color" class="white--text mt-2" icon v-if="product.product.last_edit_user">
						{{product.product.last_edit_user.name}}
					</v-chip>
					<v-btn icon v-if="product.product.revision">
						<v-icon color="red">mdi-alert-circle-outline</v-icon>
					</v-btn>
					</v-row>
				</template>
				<template v-if="context == 'provider'">
					<v-checkbox v-model="product.product.selected" @click="clickCheckBox"></v-checkbox>
					<OpenFormulaSubstances v-model="showSubstances" :product="product" @save="showSubstances = false" :loading="false" :saving="false" />
				</template>
				<template v-if="context == 'appointment'">
					<v-tooltip top>
						<template v-slot:activator="{ on }">
							<v-btn icon @click="removeProduct" v-on="on" :disabled="cantEdit">
								<v-icon color="primary">delete</v-icon>
							</v-btn>
						</template>
						<span v-translate>Remove</span>
					</v-tooltip>
				</template>
			</v-list-item-action>
			<v-dialog v-model="showProductInfo" max-width="1000" scrollable>
				<v-card>
					<v-card-title class="title primary--text mb-2 secondary">
						<translate key="sup" v-if="type === 'supplement'">Product Info</translate>
						<translate key="form" v-else>Formula Info</translate>
					</v-card-title>
					<v-progress-linear
						:active="loadingProductInfo"
						:indeterminate="loadingProductInfo"
						top
						color="primary"
					></v-progress-linear>
					<v-card-text v-if="!loadingProductInfo && type === 'supplement'" class="pt-2">
						<h3 class="primary--text" v-translate>General</h3>
						<v-simple-table>
							<template v-slot:default>
								<thead>
									<tr>
										<th class="text-left" v-translate key="name">Name</th>
										<th class="text-left" v-translate key="provider">Provider</th>
										<th v-if="!product.product.complex_product" class="text-left" v-translate key="unit">Unit</th>
										<th v-if="!product.product.complex_product" class="text-left" v-translate key="units">Units p/ box</th>
										<th v-if="!product.product.complex_product" class="text-left" v-translate key="presentation">Presentation</th>
										<th v-if="!product.product.complex_product" class="text-left" v-translate key="storage">Storage</th>
									</tr>
								</thead>
								<tbody>
									<tr>
										<td>{{ product.product.name }}</td>
										<td>
											<span v-if="product.product.external_entity">{{ product.product.external_entity.name }}</span>
											<span v-else v-translate>Not defined</span>
										</td>
										<td v-if="!product.product.complex_product">
											<span v-if="product.product.unit">{{ product.product.unit.name }} ({{ product.product.unit.code }})</span>
											<span v-else v-translate>Not defined</span>
										</td>
										<td v-if="!product.product.complex_product">
											<span v-if="product.product.units_box">{{ product.product.units_box }}</span>
											<span v-else v-translate>Not defined</span>
										</td>
										<td v-if="!product.product.complex_product">
											<span v-if="product.product.presentation">{{ product.product.presentation.name }}</span>
											<span v-else v-translate>Not defined</span>
										</td>
										<td v-if="!product.product.complex_product">
											<span v-if="product.product.storage">{{ product.product.storage.name }}</span>
											<span v-else v-translate>Not defined</span>
										</td>
									</tr>
								</tbody>
							</template>
						</v-simple-table>
						<v-row no-gutters>
							<v-col v-if="!product.product.complex_product" cols="12" md="6" class="pr-12">
								<v-row no-gutters class="my-4" justify="center" align="center">
									<h3 class="primary--text" v-translate>Ingredients</h3>
									<v-spacer></v-spacer>
									<span v-if="ingredients.length > 0 && product.product.unit" class="text-caption">Quantidade por {{product.product.daily_dose}} {{product.product.unit.code}}</span>
								</v-row>
								<p v-if="ingredients.length === 0" class="font-weight-thin ml-2" v-translate>No ingredients specified.</p>
								<template v-else>
									<div v-for="(ingredient, index) in ingredients" :key="index">
										<v-list-item dense>
											<v-list-item-content>
												<v-list-item-title>
													<v-row no-gutters>
													{{ingredient.product.name}}
													<template v-if="ingredient.processing">
														<span class="mx-1">|</span>
														<span>
															{{ingredient.processing.name}}
														</span>
														<span class="ml-1" v-if="ingredient.info">
															({{ingredient.info}})
														</span>
													</template>
													<v-spacer></v-spacer>
													{{ingredient.quantity}} {{ingredient.unit}}
													</v-row>
												</v-list-item-title>
												<v-list-item-subtitle v-if="ingredient.product.scientific_name">
													<span
														class="font-italic"
													>{{ingredient.product.scientific_name.charAt(0).toUpperCase() + ingredient.product.scientific_name.slice(1)}}</span>
												</v-list-item-subtitle>
												<v-row no-gutters v-if="ingredient.ingredients.length > 0">
													<v-col cols="12" no-gutters class="ma-0 pl-4" v-for="(ingredient, index) in ingredient.ingredients" :key="index">
														<v-row no-gutters>
															<span class="text-caption">{{ingredient.product.name}}</span>
															<v-spacer></v-spacer>
															<span v-if="ingredient.quantity > 0" class="text-caption">
																{{ingredient.quantity}} {{ingredient.unit}}
															</span>
														</v-row>
													</v-col>
												</v-row>
											</v-list-item-content>
										</v-list-item>
										<!-- <p :class="ingredient.ingredients.length > 0 ? 'mb-0' : ''">
											{{ingredient.name}}
											<template v-if="ingredient.processing">
												|<span>
													{{ingredient.processing.name}}
												</span>
												<span v-if="ingredient.info">
													({{ingredient.info}})
												</span>
											</template>
											{{ingredient.quantity}} {{ingredient.unit}}
										</p>
										<div class="mb-3" v-if="ingredient.ingredients.length > 0">
											<p class="ma-0 ml-2" v-for="(ingredient, index) in ingredient.ingredients" :key="index">
												- {{ingredient.name}}
												<span v-if="ingredient.quantity > 0">
													({{ingredient.quantity}} {{ingredient.unit}})
												</span>
											</p>
										</div> -->
									</div>
								</template>
							</v-col>
							<v-col v-else cols="12" md="12">
								<v-row no-gutters>
									<v-col cols="6">
										<h3 class="my-4 primary--text" v-translate>Components</h3>
									</v-col>
									<v-col cols="6">
										<h3 class="my-4 primary--text" v-translate>Recommended Posology</h3>
									</v-col>
									<p v-if="ingredients.length === 0" class="font-weight-thin ml-2" v-translate>No components specified.</p>
									<template v-else>
										<v-row no-gutters v-for="(ingredient, index) in ingredients" :key="index">
											<v-col cols="6">
												<v-card class="ma-1" outlined>
													<v-simple-table>
														<template v-slot:default>
															<thead>
																<tr>
																	<th class="text-left" v-translate key="presentation">Presentation</th>
																	<th class="text-left" v-translate key="storage">Storage</th>
																	<th class="text-left" v-translate key="unit">Unit</th>
																	<th class="text-left" v-translate key="units">Units p/ box</th>
																</tr>
															</thead>
															<tbody>
																<tr>
																	<td>
																		<span v-if="ingredient.presentation">{{ ingredient.presentation.name }}</span>
																		<span v-else v-translate>Not defined</span>
																	</td>
																	<td>
																		<span v-if="ingredient.storage">{{ ingredient.storage.name }}</span>
																		<span v-else v-translate>Not defined</span>
																	</td>
																	<td>
																		<span v-if="ingredient.unit">{{ ingredient.unit.name }} ({{ ingredient.unit.code }})</span>
																		<span v-else v-translate>Not defined</span>
																	</td>
																	<td>
																		<span v-if="ingredient.units_box">{{ ingredient.units_box }}</span>
																		<span v-else v-translate>Not defined</span>
																	</td>
																</tr>
															</tbody>
														</template>
													</v-simple-table>
													<v-row no-gutters class="my-2 px-2" justify="center" align="center">
														<span class="primary--text" v-translate>Ingredients</span>
														<v-spacer></v-spacer>
														<span v-if="ingredient.ingredients.length > 0 && ingredient.unit" class="text-caption">Quantidade por {{ingredient.daily_dose}} {{ingredient.unit.code}}</span>
													</v-row>
													<div v-for="(ingredient, index) in ingredient.ingredients" :key="index">
														<v-list-item dense>
															<v-list-item-content>
																<v-list-item-title>
																	<v-row no-gutters>
																	{{ingredient.product.name}}
																	<template v-if="ingredient.processing">
																		<span class="mx-1">|</span>
																		<span>
																			{{ingredient.processing.name}}
																		</span>
																		<span class="ml-1" v-if="ingredient.info">
																			({{ingredient.info}})
																		</span>
																	</template>
																	<v-spacer></v-spacer>
																	{{ingredient.quantity}} {{ingredient.unit}}
																	</v-row>
																</v-list-item-title>
																<v-list-item-subtitle v-if="ingredient.product.scientific_name">
																	<span
																		class="font-italic"
																	>{{ingredient.product.scientific_name.charAt(0).toUpperCase() + ingredient.product.scientific_name.slice(1)}}</span>
																</v-list-item-subtitle>
																<v-row no-gutters v-if="ingredient.ingredients.length > 0">
																	<v-col cols="12" no-gutters class="ma-0 pl-4" v-for="(ingredient, index) in ingredient.ingredients" :key="index">
																		<v-row no-gutters>
																			<span class="text-caption">{{ingredient.product.name}}</span>
																			<v-spacer></v-spacer>
																			<span v-if="ingredient.quantity > 0" class="text-caption">
																				{{ingredient.quantity}} {{ingredient.unit}}
																			</span>
																		</v-row>
																	</v-col>
																</v-row>
															</v-list-item-content>
														</v-list-item>
													</div>
												</v-card>
											</v-col>
											<v-col cols="6">
												<v-card class="ma-1" outlined>
													<v-simple-table v-if="existPosologyRecommendationComplex(ingredient.recommendation_quantity)">
														<template v-slot:default>
															<thead>
																<tr>
																	<th class="text-left" v-translate>M</th>
																	<th class="text-left" v-translate>MM</th>
																	<th class="text-left" v-translate>HD</th>
																	<th class="text-left" v-translate>MA</th>
																	<th class="text-left" v-translate>N</th>
																</tr>
															</thead>
															<tbody>
																<tr>
																	<td>{{ingredient.recommendation_quantity.morning}}</td>
																	<td>{{ingredient.recommendation_quantity.middle_morning}}</td>
																	<td>{{ingredient.recommendation_quantity.half_day}}</td>
																	<td>{{ingredient.recommendation_quantity.middle_afternoon}}</td>
																	<td>{{ingredient.recommendation_quantity.night}}</td>
																</tr>
															</tbody>
														</template>
													</v-simple-table>
													<template v-if="existPosologyLimitsComplex(ingredient.recommendation_quantity)">
														<p class="mb-0 mt-2 ml-2">
															<strong v-translate>Limits</strong>
														</p>
														<v-simple-table>
															<template v-slot:default>
																<thead>
																	<tr></tr>
																</thead>
																<tbody>
																	<tr v-if="ingredient.recommendation_quantity.over16 != ''">
																		<td class="font-weight-bold">>16 <translate>years</translate></td>
																		<td>{{ingredient.recommendation_quantity.over16}}</td>
																	</tr>
																	<tr v-if="ingredient.recommendation_quantity.between12_15 != ''">
																		<td class="font-weight-bold">12-15 <translate>years</translate></td>
																		<td>{{ingredient.recommendation_quantity.between12_15}}</td>
																	</tr>
																	<tr v-if="ingredient.recommendation_quantity.between6_11 != ''">
																		<td class="font-weight-bold">6-11 <translate>years</translate></td>
																		<td>{{ingredient.recommendation_quantity.between6_11}}</td>
																	</tr>
																	<tr v-if="ingredient.recommendation_quantity.under6 != ''">
																		<td class="font-weight-bold">6 <translate>years</translate></td>
																		<td>{{ingredient.recommendation_quantity.under6}}</td>
																	</tr>
																	<tr v-if="ingredient.recommendation_quantity.pos_pregnant != ''">
																		<td class="font-weight-bold" v-translate>Pregnant</td>
																		<td>{{ingredient.recommendation_quantity.pos_pregnant}}</td>
																	</tr>
																	<tr v-if="ingredient.recommendation_quantity.pos_lactant != ''">
																		<td class="font-weight-bold" v-translate>Lactant</td>
																		<td>{{ingredient.recommendation_quantity.pos_lactant}}</td>
																	</tr>
																</tbody>
															</template>
														</v-simple-table>
													</template>
												</v-card>
												<p v-if="!existPosologyRecommendationComplex(ingredient.recommendation_quantity) && !existPosologyLimitsComplex(ingredient.recommendation_quantity)" class="font-weight-thin ml-2" v-translate>No recommended posology.</p>
											</v-col>
										</v-row>
									</template>
								</v-row>
							</v-col>
							<v-col v-if="!product.product.complex_product" cols="12" md="6">
								<h3 class="my-4 primary--text" v-translate>Recommended Posology</h3>
								<v-simple-table v-if="existPosologyRecommendation">
									<template v-slot:default>
										<thead>
											<tr>
												<th class="text-left" v-translate>M</th>
												<th class="text-left" v-translate>MM</th>
												<th class="text-left" v-translate>HD</th>
												<th class="text-left" v-translate>MA</th>
												<th class="text-left" v-translate>N</th>
											</tr>
										</thead>
										<tbody>
											<tr>
												<td>{{recommendation_quantity.morning}}</td>
												<td>{{recommendation_quantity.middle_morning}}</td>
												<td>{{recommendation_quantity.half_day}}</td>
												<td>{{recommendation_quantity.middle_afternoon}}</td>
												<td>{{recommendation_quantity.night}}</td>
											</tr>
										</tbody>
									</template>
								</v-simple-table>
								<template v-if="existPosologyLimits">
									<p class="mb-0 mt-2">
										<strong v-translate>Limits</strong>
									</p>
									<v-simple-table>
										<template v-slot:default>
											<thead>
												<tr></tr>
											</thead>
											<tbody>
												<tr v-if="recommendation_quantity.over16 != ''">
													<td class="font-weight-bold">>16 <translate>years</translate></td>
													<td>{{recommendation_quantity.over16}}</td>
												</tr>
												<tr v-if="recommendation_quantity.between12_15 != ''">
													<td class="font-weight-bold">12-15 <translate>years</translate></td>
													<td>{{recommendation_quantity.between12_15}}</td>
												</tr>
												<tr v-if="recommendation_quantity.between6_11 != ''">
													<td class="font-weight-bold">6-11 <translate>years</translate></td>
													<td>{{recommendation_quantity.between6_11}}</td>
												</tr>
												<tr v-if="recommendation_quantity.under6 != ''">
													<td class="font-weight-bold">6 <translate>years</translate></td>
													<td>{{recommendation_quantity.under6}}</td>
												</tr>
												<tr v-if="recommendation_quantity.pos_pregnant != ''">
													<td class="font-weight-bold" v-translate>Pregnant</td>
													<td>{{recommendation_quantity.pos_pregnant}}</td>
												</tr>
												<tr v-if="recommendation_quantity.pos_lactant != ''">
													<td class="font-weight-bold" v-translate>Lactant</td>
													<td>{{recommendation_quantity.pos_lactant}}</td>
												</tr>
											</tbody>
										</template>
									</v-simple-table>
								</template>
								<p v-if="!existPosologyRecommendation && !existPosologyLimits" class="font-weight-thin ml-2" v-translate>No recommended posology.</p>
							</v-col>
						</v-row>
						<v-row no-gutters>
							<v-col cols="12" md="6">
								<h3 class="my-4 primary--text" v-translate>Indications</h3>
								<p v-if="indications.length === 0" class="font-weight-thin ml-2" v-translate>No indications specified.</p>
								<template v-else>
									<p v-for="(indication, index) in indications" :key="index">- {{indication.title}}</p>
								</template>
							</v-col>
							<v-col cols="12" md="6">
								<h3 class="my-4 red--text" v-translate>Contra-Indications</h3>
								<p v-if="contraindications.length === 0" class="font-weight-thin ml-2" v-translate>No contra-indications specified.</p>
								<template v-else>
									<p
										v-for="(contraindication, index) in contraindications"
										:key="index"
									>- {{contraindication.title}}</p>
								</template>
							</v-col>
						</v-row>
					</v-card-text>
					<v-card-text v-if="!loadingProductInfo && type === 'formula'">
						<template v-if="product.product.from_lab || product.product.classic_custom || product.product.lab_custom || product.product.customizable">
							<h3 v-if="!product.product.from_lab" class="primary--text mt-3" v-translate>Custom Formula</h3>
							<h3 v-else class="primary--text mt-3" v-translate>Provider Formula</h3>
							<v-simple-table v-if="product.product.from_lab">
								<template v-slot:default>
									<thead>
										<tr>
											<th class="text-left" v-translate>Name</th>
											<th class="text-left" v-translate>Provider</th>
											<th class="text-left" v-translate>Formula Provider ID</th>
										</tr>
									</thead>
									<tbody>
										<tr>
											<td>{{ product.product.name }}</td>
											<td>{{ product.product.external_entity.name }}</td>
											<td>{{ product.product.lab_formula_id }}</td>
										</tr>
									</tbody>
								</template>
							</v-simple-table>
							<v-row no-gutters>
							<v-col cols="12" md="6" class="pr-12">
								<v-row no-gutters class="my-4" justify="center" align="center">
									<h3 class="primary--text" v-translate>Ingredients</h3>
									<v-spacer></v-spacer>
									<span v-if="ingredients.length > 0 && product.product.unit" class="text-caption">Quantidade por {{product.product.daily_dose}} {{product.product.unit.code}}</span>
									<span v-if="product.product.lab_custom" v-translate>Customized</span>
								</v-row>
								<p v-if="ingredients.length === 0" class="font-weight-thin ml-2" v-translate>No ingredients specified.</p>
								<template v-else>
									<div v-for="(ingredient, index) in ingredients" :key="index">
										<v-list-item dense>
											<v-list-item-content>
												<v-list-item-title>
													<v-row no-gutters>
													{{ingredient.name}}
													<template v-if="ingredient.processing">
														<span class="mx-1">|</span>
														<span>
															{{ingredient.processing.name}}
														</span>
														<span class="ml-1" v-if="ingredient.info">
															({{ingredient.info}})
														</span>
													</template>
													<v-spacer></v-spacer>
													{{ingredient.quantity}} {{ingredient.unit}}
													</v-row>
												</v-list-item-title>
												<v-list-item-subtitle v-if="ingredient.scientific_name">
													<span
														class="font-italic"
													>{{ingredient.product.scientific_name.charAt(0).toUpperCase() + ingredient.product.scientific_name.slice(1)}}</span>
												</v-list-item-subtitle>
											</v-list-item-content>
										</v-list-item>
									</div>
								</template>
							</v-col>
							<v-col cols="12" md="6">
								<h3 class="my-4 primary--text" v-translate>Recommended Posology</h3>
								<v-simple-table v-if="existPosologyRecommendation">
									<template v-slot:default>
										<thead>
											<tr>
												<th class="text-left" v-translate>M</th>
												<th class="text-left" v-translate>MM</th>
												<th class="text-left" v-translate>HD</th>
												<th class="text-left" v-translate>MA</th>
												<th class="text-left" v-translate>N</th>
											</tr>
										</thead>
										<tbody>
											<tr>
												<td>{{recommendation_quantity.morning}}</td>
												<td>{{recommendation_quantity.middle_morning}}</td>
												<td>{{recommendation_quantity.half_day}}</td>
												<td>{{recommendation_quantity.middle_afternoon}}</td>
												<td>{{recommendation_quantity.night}}</td>
											</tr>
										</tbody>
									</template>
								</v-simple-table>
								<template v-if="existPosologyLimits">
									<p class="mb-0 mt-2">
										<strong v-translate>Limits</strong>
									</p>
									<v-simple-table>
										<template v-slot:default>
											<thead>
												<tr></tr>
											</thead>
											<tbody>
												<tr v-if="recommendation_quantity.over16 != ''">
													<td class="font-weight-bold">>16 <translate>years</translate></td>
													<td>{{recommendation_quantity.over16}}</td>
												</tr>
												<tr v-if="recommendation_quantity.between12_15 != ''">
													<td class="font-weight-bold">12-15 <translate>years</translate></td>
													<td>{{recommendation_quantity.between12_15}}</td>
												</tr>
												<tr v-if="recommendation_quantity.between6_11 != ''">
													<td class="font-weight-bold">6-11 <translate>years</translate></td>
													<td>{{recommendation_quantity.between6_11}}</td>
												</tr>
												<tr v-if="recommendation_quantity.under6 != ''">
													<td class="font-weight-bold">6 <translate>years</translate></td>
													<td>{{recommendation_quantity.under6}}</td>
												</tr>
												<tr v-if="recommendation_quantity.pos_pregnant != ''">
													<td class="font-weight-bold" v-translate>Pregnant</td>
													<td>{{recommendation_quantity.pos_pregnant}}</td>
												</tr>
												<tr v-if="recommendation_quantity.pos_lactant != ''">
													<td class="font-weight-bold" v-translate>Lactant</td>
													<td>{{recommendation_quantity.pos_lactant}}</td>
												</tr>
											</tbody>
										</template>
									</v-simple-table>
								</template>
								<p v-if="!existPosologyRecommendation && !existPosologyLimits" class="font-weight-thin ml-2" v-translate>No recommended posology.</p>
							</v-col>
							</v-row>
						</template>
						<h3 class="primary--text mt-4" v-translate>Classic Formula</h3>
						<v-simple-table>
							<template v-slot:default>
								<thead>
									<tr>
										<th class="text-left" v-translate>Name</th>
										<th class="text-left" v-translate>Translation</th>
										<th class="text-left" v-translate>Sub-Category</th>
									</tr>
								</thead>
								<tbody>
									<tr>
										<td>{{ product.product.name }}</td>
										<td>{{ product.product.translation }}</td>
										<td>{{ product.product.sub_category }}</td>
									</tr>
								</tbody>
							</template>
						</v-simple-table>
						<template v-if="product.product.pattern">
							<h4 class="mt-3" v-translate>Pattern</h4>
							<p>{{product.product.pattern}}</p>
						</template>
						<template v-if="product.product.actions">
							<h4 v-translate>Actions</h4>
							<p>{{product.product.actions}}</p>
						</template>
						<template v-if="product.product.tongue">
							<h4 v-translate>Possible Tongue Appearance</h4>
							<p>{{product.product.tongue}}</p>
						</template>
						<template v-if="product.product.pulse">
							<h4 v-translate>Possible Pulse Appearance</h4>
							<p>{{product.product.pulse}}</p>
						</template>
						<template v-if="product.product.prep_notes">
							<h4 v-translate>Preparation Notes</h4>
							<p>{{product.product.prep_notes}}</p>
						</template>
						<template v-if="product.product.original">
							<h4 v-translate>Originally Appeared In</h4>
							<p>{{product.product.original}}</p>
						</template>
						<template v-if="product.product.channels">
							<h4 v-translate>Combined Channels of all herbs in this formula</h4>
							<p>{{product.product.channels}}</p>
						</template>
						<template v-if="product.product.western_uses">
							<h4 v-translate>Western Uses</h4>
							<p>{{product.product.western_uses}}</p>
						</template>
						<template v-if="product.product.notes">
							<h4 v-translate>Aditional Notes</h4>
							<p>{{product.product.notes}}</p>
						</template>
						<template v-if="product.product.temperature">
							<h4 v-translate>Temperature</h4>
							<p>{{product.product.temperature}}</p>
						</template>
						<h3 class="primary--text mt-3" v-translate>Substances</h3>
						<h4 class="mt-3">
							<translate>Total Number of Substances</translate>:
							{{product.product.num_substances}}
						</h4>
						<template v-if="ingredients.length > 0">
							<h4 class="mt-3" v-translate>List of Substances:</h4>
							<p v-for="ing in ingredients" :key="ing.id" class="my-0 ml-2">{{ing.name}}</p>
						</template>
						<v-row no-gutters>
							<v-col cols="12" md="6">
								<h3 class="my-4 primary--text" v-translate>Indications</h3>
								<p v-if="indications.length === 0" class="font-weight-thin ml-2" v-translate>No indications specified.</p>
								<p v-for="(indication, index) in indications" :key="index">- {{indication.title}}</p>
							</v-col>
							<v-col cols="12" md="6">
								<h3 class="my-4 red--text" v-translate>Contra-Indications</h3>
								<p
									v-if="contraindications.length === 0"
									class="font-weight-thin ml-2"
									v-translate
								>No contra-indications specified.</p>
								<p
									v-for="(contraindication, index) in contraindications"
									:key="index"
								>- {{contraindication.title}}</p>
							</v-col>
						</v-row>
					</v-card-text>
					<v-divider></v-divider>
					<v-card-actions>
						<v-spacer></v-spacer>
						<v-btn color="primary darken-1" text @click="showProductInfo = false">
							<translate>Close</translate>
						</v-btn>
					</v-card-actions>
				</v-card>
			</v-dialog>
		</v-list-item>
	</v-card>
</template>

<script>
import axios from "axios";
import DeleteConfirmationDialog from "@/components/DeleteConfirmationDialog.vue";
import Vue from "vue";
import OpenFormulaSubstances from '@/components/products/OpenFormulaSubstances.vue';

export default {
	name: "ProductItem",
	props: ["product", "type", "context", "admin"],
	components: {
		DeleteConfirmationDialog,
		OpenFormulaSubstances
	},
	data: () => ({
		posologyFormValid: false,
		showInteractionDrugs: false,
		showInteractionDrugsBibliographicReferences: false,
		interactionDrugBibliographicReferences: [],
		loadingDrugBibliographicReferences: false,
		showContraindications: false,
		showProductInfo: false,
		loadingProductInfo: true,
		editAditionalInfo: false,
		recommendation_quantity: {},
		ingredients: [],
		indications: [],
		contraindications: [],
		showDeleteProduct: false,
		showSubstances: false,
		showOpenFormulaSubstances: false,
		loadingSaveOpenFormula: false,
		loadingOpenFormulaSubstances: false
	}),
	computed: {
		editProduct() {
			if (!this.admin) {
				if (this.type == "supplement") {
					return {
						name: "edit-product",
						params: { sid: this.product.product.id }
					};
				} else {
					return {
						name: "edit-formula",
						params: { sid: this.product.product.id }
					};
				}
			} else {
				if (this.type == "supplement") {
					return {
						name: "admin-edit-product",
						params: { sid: this.product.product.id, admin: true, language: "en" }
					};
				} else {
					return {
						name: "admin-edit-formula",
						params: { sid: this.product.product.id }
					};
				}
			}
		},
		existPosologyRecommendation() {
			if(this.recommendation_quantity.morning != 0 ||
			this.recommendation_quantity.middle_morning != 0 ||
			this.recommendation_quantity.half_day != 0 ||
			this.recommendation_quantity.middle_afternoon != 0 ||
			this.recommendation_quantity.night != 0) return true
			else return false
		},
		existPosologyLimits() {
			if(this.recommendation_quantity.over16 != '' ||
			this.recommendation_quantity.between12_15 != '' ||
			this.recommendation_quantity.between6_11 != '' ||
			this.recommendation_quantity.under6 != '' ||
			this.recommendation_quantity.pos_pregnant != '' ||
			this.recommendation_quantity.pos_lactant != '') return true
			else return false
		},
		cantEdit() {
			if(this.type == 'supplement') return !this.$store.getters.hasModule('fito');
			else return !this.$store.getters.hasModule('mtc');
		},
		externalEntity() {
			return this.$store.getters.externalEntity;
		}
	},
	methods: {
		validate() {
			return this.$refs.posologyForm.validate();
		},
		removeProduct() {
			this.$emit("remove-product", this.product);
		},
		editPosology(product, frequency) {
			if (this.validate()) {
				this.$emit("edit-posology", product, frequency);
			}
		},
		getInteractionDrugBibliographicReferences(id) {
			console.log("get", id);
			this.loadingDrugBibliographicReferences = true;
			axios
				.get("/product-medicine/" + id + "/bibliographic-references")
				.then(response => {
					console.log(response.data);
					this.interactionDrugBibliographicReferences = response.data;
					this.loadingDrugBibliographicReferences = false;
					this.showInteractionDrugsBibliographicReferences = true;
				})
				.catch(error => {
					console.log(error);
					this.loadingDrugBibliographicReferences = false;
				});
		},
		openLink(link) {
			window.open(link, "_blank");
		},
		existPosologyRecommendationComplex(recommendation_quantity) {
			if(recommendation_quantity.morning != 0 ||
				recommendation_quantity.middle_morning != 0 ||
				recommendation_quantity.half_day != 0 ||
				recommendation_quantity.middle_afternoon != 0 ||
				recommendation_quantity.night != 0) return true
			else return false
		},
		existPosologyLimitsComplex(recommendation_quantity) {
			if(recommendation_quantity.over16 != '' ||
				recommendation_quantity.between12_15 != '' ||
				recommendation_quantity.between6_11 != '' ||
				recommendation_quantity.under6 != '' ||
				recommendation_quantity.pos_pregnant != '' ||
				recommendation_quantity.pos_lactant != '') return true
			else return false
		},
		clickCheckBox() {
			if(this.product.product.selected) this.showSubstances = true;
		},
		showEditSubstances() {
			this.showOpenFormulaSubstances = true;
			this.getOpenFormulaSubstances();
		},
		getOpenFormulaSubstances() {
			this.loadingOpenFormulaSubstances = true;
			axios
				.get(`/mtc/formulas/${this.product.product.id}/substances`)
				.then(response => {
					console.log(response.data);
					response.data.forEach(s => {
						Vue.set(s, 'selected', true);
          });
					this.product.product.ingredients = response.data;
					this.loadingOpenFormulaSubstances = false;
				})
				.catch(error => {
					console.log(error);
					this.loadingOpenFormulaSubstances = false;
				});
		},
		saveOpenFormula() {
			this.loadingSaveOpenFormula = true;
			axios
				.post(`/mtc/formulas/${this.product.product.id}/substances`, this.product.product.ingredients.filter(i => i.selected))
				.then(response => {
					console.log(response.data);
					this.loadingSaveOpenFormula = false;
					this.showOpenFormulaSubstances = false;
				})
				.catch(error => {
					console.log(error);
					this.loadingSaveOpenFormula = false;
				});
		}
	},
	created() {
		this.$root.$on('tour-treatments-mmc-interaction', () => {
			this.showInteractionDrugs = true;
		});
		this.$root.$on('tour-prescription', () => {
			this.showInteractionDrugs = false;
		});
	},
	watch: {
		showProductInfo() {
			if (this.showProductInfo && this.loadingProductInfo) {
				//CALL API
				var link = "";
				if (this.type === "supplement") link = "/product/";
				else link = "/mtc/formulas/";
				axios
					.get(link + this.product.product.id + "/info")
					.then(response => {
						if (response.status === 200) {
							console.log(response.data);
							this.recommendation_quantity = response.data.recommendation_quantity;
							this.ingredients = response.data.ingredients.sort(function(a,b) {
								return b.quantity - a.quantity;
							});
							this.indications = response.data.indications;
							this.contraindications = response.data.contraindications;
							this.loadingProductInfo = false;
						}
					})
					.catch(error => {
						console.log(error);
						this.loadingProductInfo = false;
					});
			}
		}
	}
};
</script>